import { useState, type ReactNode } from 'react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { ModuleGuide } from '@/shared/components/ui/ModuleGuide';
import { formatINR } from '@/shared/utils/currency';
import { localToday } from '@/shared/utils/utils';
import {
  useBankBook, useDailyCollection, useDailyPayment, useMonthlySummary, usePettyCash,
  type CashBookLine, type DailyMoneyLine, type ReportLedgerLine,
} from '../hooks/useReports';
import { ReportEmpty, ReportLoading, ReportProblem } from '../components/ReportState';
import { Landmark, PiggyBank, ArrowDownLeft, ArrowUpRight, BarChart3 } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Cash & Bank Reports — read-only screens for the accounts-module report
// endpoints that had no UI: /api/accounts/reports/bankbook,
// /api/accounts/cash/petty, /reports/daily-collection, /reports/daily-payment
// and /reports/monthly-summary.
//
// Deliberately not a second Banking page. BankingPage already offers the bank
// statement (/api/accounts/bank/statement, any account code, with
// reconciliation) and the daily cash book (/api/accounts/cash/book). These are
// the fixed-ledger-book versions the report router exposes — the bank book
// always reads COA head 1020, petty cash always reads head 1030 — plus the day
// and month cut-offs. Where an endpoint 404s because the ledger account has not
// been created, that answer is shown rather than a table of zeros.
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'bankbook', label: 'Bank Book', icon: Landmark },
  { id: 'petty', label: 'Petty Cash', icon: PiggyBank },
  { id: 'collection', label: 'Daily Collection', icon: ArrowDownLeft },
  { id: 'payment', label: 'Daily Payment', icon: ArrowUpRight },
  { id: 'monthly', label: 'Monthly Summary', icon: BarChart3 },
] as const;

type TabId = (typeof TABS)[number]['id'];

/** YYYY-MM of the current month — the monthly filter's starting value. */
function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const money = (value: number | null | undefined) => formatINR(value ?? 0);

interface SummaryItem {
  label: string;
  value: string;
  tone?: 'in' | 'out';
}

function SummaryBar({ items }: { items: SummaryItem[] }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 bg-muted/30 px-4 py-2 text-sm">
      {items.map((it) => (
        <span key={it.label}>
          {it.label}:{' '}
          <strong className={it.tone === 'in' ? 'text-green-600' : it.tone === 'out' ? 'text-red-600' : undefined}>{it.value}</strong>
        </span>
      ))}
    </div>
  );
}

function LedgerRows({ rows }: { rows: ReportLedgerLine[] }) {
  return (
    <div className="max-h-[520px] overflow-auto">
      <table className="w-full text-sm text-left">
        <thead className="sticky top-0 bg-muted/50 text-xs uppercase">
          <tr>
            <th className="px-4 py-2">Date</th><th className="px-4 py-2">Narration</th>
            <th className="px-4 py-2">Voucher</th><th className="px-4 py-2 text-right">Debit</th>
            <th className="px-4 py-2 text-right">Credit</th><th className="px-4 py-2 text-right">Balance</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((e, i) => (
            <tr key={`${e.date}-${i}`} className="hover:bg-muted/20">
              <td className="whitespace-nowrap px-4 py-2">{new Date(e.date).toLocaleDateString('en-IN')}</td>
              <td className="max-w-[280px] truncate px-4 py-2">{e.narration || '—'}</td>
              <td className="px-4 py-2 text-xs capitalize">{(e.voucherType || '—').replace(/_/g, ' ')}</td>
              <td className="px-4 py-2 text-right text-green-600">{e.debit ? money(e.debit) : ''}</td>
              <td className="px-4 py-2 text-right text-red-600">{e.credit ? money(e.credit) : ''}</td>
              <td className="px-4 py-2 text-right font-medium">{money(e.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CashRows({ rows }: { rows: CashBookLine[] }) {
  return (
    <div className="max-h-[520px] overflow-auto">
      <table className="w-full text-sm text-left">
        <thead className="sticky top-0 bg-muted/50 text-xs uppercase">
          <tr>
            <th className="px-4 py-2">Time</th><th className="px-4 py-2">Narration</th>
            <th className="px-4 py-2">Voucher</th><th className="px-4 py-2 text-right">Cash In</th>
            <th className="px-4 py-2 text-right">Cash Out</th><th className="px-4 py-2 text-right">Balance</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((e, i) => (
            <tr key={e.id || i} className="hover:bg-muted/20">
              <td className="whitespace-nowrap px-4 py-2 text-xs">
                {e.time ? new Date(e.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
              </td>
              <td className="max-w-[280px] truncate px-4 py-2">{e.narration || '—'}</td>
              <td className="px-4 py-2 text-xs capitalize">{(e.voucherType || '—').replace(/_/g, ' ')}</td>
              <td className="px-4 py-2 text-right text-green-600">{e.cashIn ? money(e.cashIn) : ''}</td>
              <td className="px-4 py-2 text-right text-red-600">{e.cashOut ? money(e.cashOut) : ''}</td>
              <td className="px-4 py-2 text-right font-medium">{money(e.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The one non-happy-state sequence every book on this page shares: loading,
 * error, permission-denied, empty, then rows. `children` is the table, so the
 * caller stays in control of its columns.
 */
function BookState({
  isLoading, error, loaded, entries, summary, children,
}: {
  isLoading: boolean;
  error: unknown;
  loaded: boolean;
  entries: { length: number };
  summary: SummaryItem[];
  children: ReactNode;
}) {
  if (isLoading) return <ReportLoading />;
  if (error) return <ReportProblem error={error} subject="this book" />;
  if (!loaded) return <ReportEmpty text="The report returned no rows." />;
  if (entries.length === 0) return <ReportEmpty text="No posted entries for this period. The ledger is genuinely empty, not unread." />;
  return (
    <>
      <SummaryBar items={[...summary, { label: 'Entries', value: String(entries.length) }]} />
      {children}
    </>
  );
}

function BankBookTab() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const query = useBankBook(startDate || undefined, endDate || undefined);
  const data = query.data;
  const rows: ReportLedgerLine[] = data?.entries ?? [];

  return (
    <Card className="shadow-xs">
      <CardHeader
        title="Bank Book"
        subtitle="Posted ledger entries on the bank account (chart-of-accounts head 1020), oldest first with a running balance."
        action={
          <div className="flex items-center gap-2">
            <Input name="bb-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36" />
            <Input name="bb-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36" />
          </div>
        }
      />
      <CardBody className="p-0">
        <BookState
          isLoading={query.isLoading}
          error={query.error}
          loaded={!!data}
          entries={rows}
          summary={[
            { label: 'Opening Balance', value: money(data?.openingBalance) },
            { label: 'Closing Balance', value: money(data?.closingBalance) },
          ]}
        >
          <LedgerRows rows={rows} />
        </BookState>
      </CardBody>
    </Card>
  );
}

function PettyCashTab() {
  const [date, setDate] = useState(localToday());
  const query = usePettyCash(date);
  const data = query.data;
  const rows: CashBookLine[] = data?.entries ?? [];

  return (
    <Card className="shadow-xs">
      <CardHeader
        title={`Petty Cash — ${data?.date || date}`}
        subtitle={data?.account ? `${data.account.name} (head ${data.account.code})` : 'Posted entries on the petty cash account (chart-of-accounts head 1030) for one day.'}
        action={<Input name="pc-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />}
      />
      <CardBody className="p-0">
        <BookState
          isLoading={query.isLoading}
          error={query.error}
          loaded={!!data}
          entries={rows}
          summary={[
            { label: 'Opening', value: money(data?.openingBalance) },
            { label: 'Cash In', value: money(data?.totalCashIn), tone: 'in' },
            { label: 'Cash Out', value: money(data?.totalCashOut), tone: 'out' },
            { label: 'Closing', value: money(data?.closingBalance) },
          ]}
        >
          <CashRows rows={rows} />
        </BookState>
      </CardBody>
    </Card>
  );
}

/**
 * Daily collection and daily payment are the same screen either side of the
 * ledger. Only the query for the tab actually shown is enabled — the other is
 * passed an empty date so its hook never fires.
 */
function DailyMoneyTab({ kind }: { kind: 'collection' | 'payment' }) {
  const [date, setDate] = useState(localToday());
  const isCollection = kind === 'collection';
  const collection = useDailyCollection(isCollection ? date : '');
  const payment = useDailyPayment(isCollection ? '' : date);
  const query = isCollection ? collection : payment;
  const rows: DailyMoneyLine[] = (isCollection ? query.data?.receipts : query.data?.payments) ?? [];

  return (
    <Card className="shadow-xs">
      <CardHeader
        title={isCollection ? `Daily Collection — ${query.data?.date || date}` : `Daily Payment — ${query.data?.date || date}`}
        subtitle={isCollection
          ? 'Receipts posted on the selected day, read from the payments ledger.'
          : 'Payments posted on the selected day, read from the payments ledger.'}
        action={<Input name="dm-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />}
      />
      <CardBody className="p-0">
        {query.isLoading ? <ReportLoading />
          : query.error ? <ReportProblem error={query.error} subject={isCollection ? 'the daily collection report' : 'the daily payment report'} />
          : !query.data ? <ReportEmpty text="The report returned no rows." />
          : rows.length === 0 ? (
            <ReportEmpty text={`No ${isCollection ? 'receipts' : 'payments'} were posted on ${query.data.date}. The day is genuinely empty, not unread.`} />
          ) : (
            <>
              <SummaryBar items={[
                { label: 'Date', value: query.data.date },
                { label: 'Total', value: money(query.data.total), tone: isCollection ? 'in' : 'out' },
                { label: 'Count', value: String(query.data.count) },
              ]} />
              <div className="max-h-[520px] overflow-auto">
                <table className="w-full text-sm text-left">
                  <thead className="sticky top-0 bg-muted/50 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2">Voucher</th>
                      <th className="px-4 py-2">{isCollection ? 'Customer' : 'Vendor'}</th>
                      <th className="px-4 py-2">Method</th>
                      <th className="px-4 py-2">Reference</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((r, i) => (
                      <tr key={`${r.paymentNumber || 'row'}-${i}`} className="hover:bg-muted/20">
                        <td className="px-4 py-2 font-mono text-xs">{r.paymentNumber || '—'}</td>
                        <td className="max-w-[260px] truncate px-4 py-2">{(isCollection ? r.customer : r.vendor) || '—'}</td>
                        <td className="px-4 py-2 text-xs capitalize">{r.method || '—'}</td>
                        <td className="px-4 py-2 font-mono text-xs">{r.reference || '—'}</td>
                        <td className={`px-4 py-2 text-right font-semibold ${isCollection ? 'text-green-600' : 'text-red-600'}`}>{money(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
      </CardBody>
    </Card>
  );
}

function MonthlySummaryTab() {
  const [month, setMonth] = useState(currentMonth());
  const { data, isLoading, error } = useMonthlySummary(month);

  return (
    <Card className="shadow-xs">
      <CardHeader
        title={`Monthly Summary — ${data?.month || month}`}
        subtitle="Income and expense postings, documents issued, and money actually collected or paid out in the month."
        action={<Input name="ms-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />}
      />
      <CardBody className="space-y-4">
        {isLoading ? <ReportLoading />
          : error ? <ReportProblem error={error} subject="the monthly summary" />
          : !data ? <ReportEmpty text="The summary returned no figures." />
          : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { label: 'Total Revenue', value: money(data.totalRevenue) },
                  { label: 'Total Expenses', value: money(data.totalExpenses) },
                  { label: 'Net Profit', value: money(data.netProfit) },
                  { label: 'Collections (posted)', value: `${money(data.totalCollections)} · ${data.collectionsCount} receipts` },
                  { label: 'Payments (posted)', value: `${money(data.totalPaymentsMade)} · ${data.paymentsCount} payments` },
                  { label: 'Invoices Issued', value: String(data.invoicesIssued) },
                  { label: 'Bills Received', value: String(data.billsReceived) },
                ].map((f) => (
                  <div key={f.label} className="rounded-lg border border-outline bg-surface px-4 py-3">
                    <p className="text-label-caps uppercase text-on-surface-variant">{f.label}</p>
                    <p className="mt-1 font-mono text-data-lg font-semibold text-on-surface">{f.value}</p>
                  </div>
                ))}
              </div>

              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Expense breakdown</h4>
                {(data.expenseBreakdown ?? []).length === 0
                  ? <ReportEmpty text="No expense account posted anything in this month." />
                  : (
                    <div className="max-h-[420px] overflow-auto rounded-lg border border-border">
                      <table className="w-full text-sm text-left">
                        <thead className="sticky top-0 bg-muted/50 text-xs uppercase">
                          <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Expense Account</th><th className="px-4 py-2 text-right">Amount</th></tr>
                        </thead>
                        <tbody className="divide-y">
                          {data.expenseBreakdown.map((line, i) => (
                            <tr key={`${line.code || 'acct'}-${i}`} className="hover:bg-muted/20">
                              <td className="px-4 py-2 font-mono text-xs">{line.code || '—'}</td>
                              <td className="px-4 py-2">{line.name || '—'}</td>
                              <td className="px-4 py-2 text-right font-medium">{money(line.amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-muted/30 font-semibold">
                            <td className="px-4 py-2" colSpan={2}>Total expenses</td>
                            <td className="px-4 py-2 text-right">{money(data.totalExpenses)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
            </>
          )}
      </CardBody>
    </Card>
  );
}

export function CashBankReportsPage() {
  const [tab, setTab] = useState<TabId>('bankbook');

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Cash &amp; Bank Reports</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Bank book, petty cash, daily collection, daily payment and the monthly summary — read straight from the posted ledger.
        </p>
      </div>
      <ModuleGuide
        summary="These reports are not a separate set of numbers somebody maintains. Every figure is aggregated from posted ledger entries when the screen opens, so a voucher that is drafted but never posted cannot appear here."
        steps={[
          'The bank book reads the bank head (1020); petty cash reads the petty cash head (1030).',
          'Daily collection sums posted receipts on one day; daily payment sums posted payments.',
          'Monthly summary nets income against expense postings and counts the documents in the month.',
        ]}
        formulas={[
          { label: 'Closing balance', formula: 'Opening balance + debits − credits' },
          { label: 'Net profit', formula: 'Income postings − expense postings' },
        ]}
      />

      <div className="flex gap-2 overflow-x-auto border-b pb-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-t-md border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
              tab === t.id
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-transparent text-muted-foreground hover:bg-muted/30 hover:text-foreground'
            }`}
          >
            <t.icon className="h-4 w-4" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {tab === 'bankbook' && <BankBookTab />}
      {tab === 'petty' && <PettyCashTab />}
      {tab === 'collection' && <DailyMoneyTab kind="collection" />}
      {tab === 'payment' && <DailyMoneyTab kind="payment" />}
      {tab === 'monthly' && <MonthlySummaryTab />}
    </div>
  );
}
